import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  listCatalogPrefix,
  listCatalogRows,
  listFetchErrorKey,
  listFetchStateKey,
  listRowKey,
  listRows,
} from "../../../shared/connectors/listRows";
import { connectorAllowedModels } from "../../../shared/connectors/models";
import type { ListField } from "../../../shared/connectors/types";
import { sharedMutators } from "../../../shared/mutators";
import { Box } from "../Box";
import { Button } from "../Button";
import { Checkbox } from "../Checkbox";
import { ClickButton } from "../ClickButton";
import { Icon } from "../Icon";
import { InlineInputButton } from "../InlineInput";
import { Input } from "../Input";
import { ListTable } from "../ListTable";
import { SearchContainer, SearchInput } from "../SearchField";
import { HoverActions, ListBox } from "../Settings/shared";
import { Text } from "../Text";
import type { App } from "./Form";

const InputRow = styled("div")`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const HelperRow = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 6px 8px;
`;

const FetchLink = styled(ClickButton)`
  font-weight: 350;
  font-size: 10px;
  line-height: 12px;
  letter-spacing: -1%;
  color: var(--text-primary);
  opacity: 0.3;
  white-space: nowrap;
  flex-shrink: 0;
  &:hover {
    opacity: 0.8;
  }
`;

const AddButton = styled(InlineInputButton)`
  background: var(--alpha4);
  &:hover:not(:disabled) {
    background: var(--alpha8);
  }
`;

const PickerRow = styled("button")`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  &:hover {
    background: var(--alpha4);
  }
  &[data-checked="true"] {
    background: var(--alpha4);
  }
`;

const EmptyRow = styled("div")`
  padding: 12px;
`;

type Props = {
  item: ListField;
  app: App;
  disabled?: boolean;
  onEdit?: () => void;
};

export function ModelsListField({ item, app, disabled, onEdit }: Props) {
  const z = useZero();
  const column = item.columns[0]!;
  const rows = listRows(app.configuration, item.prefix);
  const catalog = listCatalogRows(app.configuration, item.prefix);
  const allowed = useMemo(
    () => new Set(connectorAllowedModels(app.configuration)),
    [app.configuration]
  );
  const [draft, setDraft] = useState("");
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Picking several models in quick succession fires writes before Zero pushes a
  // re-render, so each write must build on the previous one's result rather than
  // the render-time snapshot — otherwise two picks reuse the same nextId and the
  // second clobbers the first. Advance this on every write, and adopt the prop
  // again once Zero commits it.
  const configRef = useRef(app.configuration);
  const committedConfig = useRef(app.configuration);
  if (committedConfig.current !== app.configuration) {
    committedConfig.current = app.configuration;
    configRef.current = app.configuration;
  }

  // A list has no native validity, so anchor the "at least one row" rule on the
  // add-input, which the modal's reportValidity() gate already flushes on save.
  useEffect(() => {
    inputRef.current?.setCustomValidity(
      item.required && rows.length === 0 ? "Add at least one entry." : ""
    );
  }, [item.required, rows.length]);

  const base = () => ({
    connectedAppId: app.id,
    name: app.name,
    identifier: app.identifier,
    urlPattern: app.urlPattern,
  });

  const writeConfiguration = (configuration: Record<string, string>) => {
    configRef.current = configuration;
    onEdit?.();
    z.mutate(sharedMutators.updateConnectedApp({ ...base(), configuration }));
  };

  const addModel = (raw: string): boolean => {
    const value = raw.trim();
    if (!value) return false;
    const config = configRef.current;
    if (connectorAllowedModels(config).includes(value)) return false;
    const currentRows = listRows(config, item.prefix);
    const nextId = currentRows.length
      ? Math.max(...currentRows.map((row) => row.id)) + 1
      : 0;
    writeConfiguration({
      ...config,
      [listRowKey(item.prefix, nextId, column.field)]: value,
    });
    return true;
  };

  const tryAdd = () => {
    if (addModel(draft)) setDraft("");
  };

  const removeRowKeys = (rowPrefix: string) =>
    writeConfiguration(
      Object.fromEntries(
        Object.entries(configRef.current).filter(
          ([key]) => !key.startsWith(rowPrefix)
        )
      )
    );

  const removeModel = (id: number) => removeRowKeys(`${item.prefix}.${id}.`);

  const toggleModel = (name: string) => {
    const config = configRef.current;
    if (connectorAllowedModels(config).includes(name)) {
      const row = listRows(config, item.prefix).find(
        (r) => r.values[column.field]?.trim() === name
      );
      if (row) removeModel(row.id);
    } else {
      addModel(name);
    }
  };

  const clearCatalog = () => {
    const catalogPrefix = `${listCatalogPrefix(item.prefix)}.`;
    setSearch("");
    writeConfiguration(
      Object.fromEntries(
        Object.entries(configRef.current).filter(
          ([key]) => !key.startsWith(catalogPrefix)
        )
      )
    );
  };

  const fetchState = app.configuration[listFetchStateKey(item.prefix)];
  const fetching = fetchState === "fetching";
  const fetchError =
    fetchState === "error"
      ? app.configuration[listFetchErrorKey(item.prefix)]
      : null;

  const filteredCatalog = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return catalog;
    return catalog.filter((row) =>
      (row.values[column.field] ?? "").toLowerCase().includes(needle)
    );
  }, [catalog, search, column.field]);

  return (
    <div>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md" color="var(--text-primary)">
          {item.label}
        </Text>
      </Box>

      {!disabled && (
        <InputRow>
          <Input
            ref={inputRef}
            type="text"
            placeholder={
              column.placeholder
                ? `Add a model ID (e.g. ${column.placeholder})`
                : "Add a model ID"
            }
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                tryAdd();
              }
            }}
            style={{ width: "100%", paddingRight: 36 }}
          />
          <AddButton
            type="button"
            disabled={!draft.trim()}
            aria-label="Add model"
            onClick={tryAdd}
          >
            <Icon name="plus" size={16} />
          </AddButton>
        </InputRow>
      )}

      <HelperRow>
        <Text
          variant="text-small"
          color={fetchError ? "var(--red-600)" : "var(--text-secondary)"}
        >
          {fetchError ?? item.helperText}
        </Text>
        {item.fetch && !disabled && (
          <FetchLink
            disabled={fetching}
            onClick={() => {
              onEdit?.();
              z.mutate(
                sharedMutators.refreshConnectorList({
                  connectedAppId: app.id,
                  prefix: item.prefix,
                })
              );
            }}
          >
            {fetching ? "Fetching…" : item.fetch.label}
          </FetchLink>
        )}
      </HelperRow>

      {catalog.length > 0 && !disabled && (
        <Box top={8}>
          <ListBox style={{ flex: "none", overflow: "hidden" }}>
            <SearchContainer>
              <Icon name="search" size={16} />
              <SearchInput
                type="text"
                placeholder={`Search ${catalog.length} models…`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <ClickButton
                type="button"
                aria-label="Dismiss fetched models"
                onClick={clearCatalog}
              >
                <Icon name="x" size={16} />
              </ClickButton>
            </SearchContainer>
            <div style={{ maxHeight: 280, overflow: "auto" }}>
              {filteredCatalog.map((row) => {
                const name = row.values[column.field] ?? "";
                const checked = allowed.has(name);
                return (
                  <PickerRow
                    key={row.id}
                    type="button"
                    data-checked={checked}
                    onClick={() => toggleModel(name)}
                  >
                    <Checkbox checked={checked} />
                    <Text
                      variant="text-regular"
                      color="var(--text-primary)"
                      ellipsis
                    >
                      {name}
                    </Text>
                  </PickerRow>
                );
              })}
              {filteredCatalog.length === 0 && (
                <EmptyRow>
                  <Text variant="text-small" color="var(--text-secondary)">
                    No models match “{search}”.
                  </Text>
                </EmptyRow>
              )}
            </div>
          </ListBox>
        </Box>
      )}

      {rows.length > 0 && (
        <Box top={8}>
          <ListBox
            data-compact="true"
            style={{ flex: "none", maxHeight: 320, overflow: "auto" }}
          >
            <ListTable
              headers={{ name: "Model", actions: "" }}
              columns={{ name: "flex" }}
              items={rows.map((row) => ({
                key: String(row.id),
                name: (
                  <Text
                    variant="text-regular"
                    color="var(--text-primary)"
                    ellipsis
                  >
                    {row.values[column.field]}
                  </Text>
                ),
                actions: (hasMouse: boolean) =>
                  disabled ? null : (
                    <HoverActions visible={hasMouse}>
                      <Button
                        type="button"
                        variant="secondary"
                        icon="x"
                        onClick={() => removeModel(row.id)}
                      />
                    </HoverActions>
                  ),
              }))}
            />
          </ListBox>
          <Box horizontal={8} top={6}>
            <Text variant="text-small" color="var(--text-secondary)">
              Only these models can be used with this connector.
            </Text>
          </Box>
        </Box>
      )}
    </div>
  );
}
